import { PlusIcon, RotateCcwIcon, SettingsIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";

import { Button } from "../ui/button";
import { SidebarInset } from "../ui/sidebar";
import { Spinner } from "../ui/spinner";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";
import { openHostedAddProjectDialog } from "../../hosted/hostedAddProjectStore";
import { useHostedProjectStore } from "../../hosted/hostedProjectStore";
import { useHostedProjects } from "../../hosted/useHostedProjects";
import { useHostedChatGptStatus } from "../../hosted/useHostedChatGptStatus";
import { HostedChatGptControl } from "./HostedChatGptControl";
import { HostedLocalCompanionCard } from "./HostedLocalCompanionCard";
import { HostedStudioPanel } from "./HostedStudioPanel";

/**
 * The hosted project surface shown when a Roblox project exists but no local T3
 * environment is connected. This is the state that previously dead-ended on "No
 * environment is available": Studio pairing, provider readiness, and memory are
 * all hosted concerns and work without a companion.
 */
export function HostedProjectWorkspace() {
  const { projects, status, errorMessage, refresh, selectProject } = useHostedProjects();
  const selectedProjectId = useHostedProjectStore((state) => state.selectedProjectId);
  const activeProject = projects.find((project) => project.id === selectedProjectId) ?? null;
  const { state: chatGptState, errorMessage: chatGptStatusError } = useHostedChatGptStatus();

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <header
          className={cn(
            "flex items-center gap-2 border-b border-border px-3 py-2 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none sm:px-5 sm:py-3",
            COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
          )}
        >
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {activeProject?.name ?? "Roblox projects"}
          </span>
          <Button
            aria-label="Add project"
            size="xs"
            variant="ghost"
            onClick={openHostedAddProjectDialog}
          >
            <PlusIcon className="size-3.5" />
            New project
          </Button>
          <Button
            render={<Link to="/settings/memory" />}
            aria-label="Open memory settings"
            size="icon-xs"
            variant="ghost"
            className="size-6 text-muted-foreground hover:text-foreground"
          >
            <SettingsIcon className="size-3.5" />
          </Button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-5">
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
            {status === "loading" && projects.length === 0 ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground/80">
                <Spinner className="size-3.5" />
                Loading your projects…
              </p>
            ) : null}

            {status === "error" ? (
              <div
                className="flex flex-wrap items-center gap-2 rounded-xl border border-destructive/32 bg-destructive/4 p-4"
                role="alert"
              >
                <p className="min-w-0 flex-1 text-xs text-destructive-foreground">
                  {errorMessage ?? "Could not load your projects."}
                </p>
                <Button size="xs" variant="outline" onClick={() => void refresh()}>
                  <RotateCcwIcon className="size-3" />
                  Try again
                </Button>
              </div>
            ) : null}

            {status === "ready" && projects.length === 0 ? (
              <div className="rounded-xl border border-border/70 bg-card/25 p-5">
                <h2 className="text-sm font-medium text-foreground">No projects yet</h2>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground/80">
                  Create a named Roblox project, then pair Studio to it.
                </p>
                <div className="mt-3">
                  <Button size="sm" onClick={openHostedAddProjectDialog}>
                    <PlusIcon className="size-4" />
                    Add project
                  </Button>
                </div>
              </div>
            ) : null}

            {projects.length > 1 ? (
              <nav aria-label="Projects" className="flex flex-wrap gap-1.5">
                {projects.map((project) => (
                  <Button
                    key={project.id}
                    size="xs"
                    variant={project.id === selectedProjectId ? "secondary" : "ghost"}
                    aria-current={project.id === selectedProjectId ? "true" : undefined}
                    onClick={() => selectProject(project.id)}
                  >
                    {project.name}
                  </Button>
                ))}
              </nav>
            ) : null}

            {activeProject ? (
              <>
                <HostedStudioPanel projectId={activeProject.id} />
                <HostedLocalCompanionCard />
                <HostedChatGptControl backendState={chatGptState} />
                {chatGptStatusError ? (
                  <p className="text-xs text-muted-foreground/70" role="status">
                    Provider status is unavailable right now: {chatGptStatusError}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </SidebarInset>
  );
}
